import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { map, Observable, of, shareReplay, tap } from 'rxjs'
import { AuthService } from '../../../services/auth.service'
import { ServerUrlService } from '../../../services/server-url.service'
import { CallPatientBody, GenexusParams, ScheduleType } from '../interfaces/genexus'

@Injectable({
  providedIn: 'root',
})
export class GenexusService {
  private cachedVar0008: string | null = null
  private var0008$: Observable<string> | null = null

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private serverUrl: ServerUrlService,
    private router: Router
  ) {}

  private getBaseUrl(): Observable<string> {
    if (this.cachedVar0008) return of(this.cachedVar0008)
    if (!this.var0008$) {
      this.var0008$ = this.http.get<{ value: string }>(`${this.serverUrl.url}/global/var0008`).pipe(
        map((res) => res.value),
        tap((value) => (this.cachedVar0008 = value)),
        shareReplay(1)
      )
    }
    return this.var0008$
  }

  private get session(): string {
    const urlTree = this.router.parseUrl(this.router.url)
    const params = urlTree.queryParams
    const si = Object.keys(params).find((key) => key.toUpperCase() === 'SI')
    const value = si ? params[si] : undefined
    if (!value) {
      console.error('GenexusService: query param "SI" not found in the current route')
    }
    return value || ''
  }

  private buildUrl(params: GenexusParams): Observable<string> {
    return this.buildQueryUrl(params)
  }

  private buildQueryUrl(params: GenexusParams): Observable<string> {
    return this.getBaseUrl().pipe(
      map(
        (baseUrl) =>
          `${baseUrl}/servlet/app.atendimentofunctions?${encodeURIComponent(JSON.stringify(params))}`
      )
    )
  }

  /** Click on free time slot — returns new appointment screen URL */
  getNewScheduleUrl(
    date: string,
    patientBirthDate = '',
    patientName = '',
    usuarioid?: number,
    scheduleType?: ScheduleType,
    hour?: string,
    scheduleId?: string
  ): Observable<string> {
    return this.buildUrl({
      acao: 'novoAgendamento',
      data: date,
      filtropacdata: patientBirthDate,
      filtropacnome: patientName,
      session: this.session,
      usuarioid,
      agendatipo: scheduleType,
      hora: hour,
      agendaid: scheduleId,
    })
  }

  /** Click on free time slot from Multi Agenda — opens Centro de Serviço insert flow */
  getMultiScheduleNewServiceUrl(date: string, hour: string, scheduleId: string): Observable<string> {
    return this.buildQueryUrl({
      acao: 'atender',
      agendatipo: 'cs',
      accnum: 0,
      data: date,
      hora: hour,
      agendaid: scheduleId,
      session: this.session,
      telaOrigem: 'multiagenda',
    })
  }

  /** Click on $ icon — returns cashier/billing screen URL */
  getCashierUrl(
    scheduleType: ScheduleType,
    date: string,
    accnum: string,
    usuarioid?: number
  ): Observable<string> {
    return this.buildUrl({
      acao: 'caixa',
      agendatipo: scheduleType,
      data: date,
      accnum,
      session: this.session,
      usuarioid,
    })
  }

  /** Click on history button — returns appointment history screen URL */
  getHistoryUrl(
    scheduleType: ScheduleType,
    date: string,
    hora: string,
    agendaid: string,
    accnum: string,
    usuarioid?: number
  ): Observable<string> {
    return this.buildUrl({
      acao: 'historico',
      agendatipo: scheduleType,
      data: date,
      hora,
      agendaid,
      accnum,
      session: this.session,
      usuarioid,
    })
  }

  /** Click on status — returns change status screen URL */
  getChangeStatusUrl(
    scheduleType: ScheduleType,
    date: string,
    hora: string,
    agendaid: string,
    usuarioid?: number
  ): Observable<string> {
    return this.buildUrl({
      acao: 'mudarstatus',
      agendatipo: scheduleType,
      data: date,
      hora,
      agendaid,
      session: this.session,
      usuarioid,
    })
  }

  /** Click on appointment — returns appointment detail/attend screen URL */
  getAppointmentDetailUrl(
    scheduleType: ScheduleType,
    accnum: string,
    date: string,
    hora: string,
    agendaid: string,
    usuarioid?: number
  ): Observable<string> {
    return this.buildUrl({
      acao: 'atender',
      agendatipo: scheduleType,
      accnum,
      data: date,
      hora,
      agendaid,
      session: this.session,
      usuarioid,
    })
  }

  /** Click on + button — returns additional functions screen URL */
  getAdditionalFunctionsUrl(
    scheduleType: ScheduleType,
    accnum: string,
    usuarioid?: number
  ): Observable<string> {
    return this.buildUrl({
      acao: 'funcoesAdicionais',
      agendatipo: scheduleType,
      accnum,
      session: this.session,
      usuarioid,
    })
  }

  /** Click on stethoscope icon — returns exam office screen URL */
  getOfficeUrl(accnum: string): Observable<string> {
    return this.getBaseUrl().pipe(
      map((baseUrl) => `${baseUrl}/servlet/app.redirecinonalink?consultorioexame_${accnum}`)
    )
  }

  /**
   * Navigate the top-most window to a GeneXus URL.
   * Multi Agenda is loaded inside a GX webpanel iframe; assigning `window.location.href`
   * from inside the iframe keeps GX's chrome around the new page, duplicating its menu.
   * Writing to `window.top.location.href` is allowed cross-origin and replaces the
   * outer shell — same effect as the OG GX "Atendimento HTML" redirect.
   */
  navigateTop(url: string): void {
    const target = (typeof window !== 'undefined' && window.top) || window
    target.location.href = url
  }

  /** Click on megaphone icon — calls patient on the panel */
  callPatient(
    scheduleType: ScheduleType,
    scheduleId: number,
    scheduleName: string,
    patientId: number,
    patientName: string,
    appointmentId: number
  ): Observable<any> {
    const body: CallPatientBody = {
      userId: this.authService.user!.id,
      scheduleType,
      scheduleId,
      scheduleName,
      patientId,
      patientName,
      appointmentId,
    }
    return this.http.post(`${this.serverUrl.url}/call-patient`, body)
  }
}
